import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  useTheme,
  useMediaQuery,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import WebIcon from '@mui/icons-material/Web';
import SpeedIcon from '@mui/icons-material/Speed';
import SecurityIcon from '@mui/icons-material/Security';
import DevicesIcon from '@mui/icons-material/Devices';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import SchoolIcon from '@mui/icons-material/School';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MemoryIcon from '@mui/icons-material/Memory';
import TuneIcon from '@mui/icons-material/Tune';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import BugReportIcon from '@mui/icons-material/BugReport';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

const MobileAppDevelopment: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Define mobile platforms
  const platforms = [
    {
      name: "iOS App Development",
      description: "Native iOS applications tailored for iPhones and iPads using Swift and SwiftUI, ensuring high performance and seamless user experience",
      icon: <AppleIcon fontSize="large" />,
      features: [
        "Native iOS Development with Swift",
        "SwiftUI for Modern Interfaces",
        "App Store Optimization",
        "Apple Authentication Integration",
        "Siri and Apple Services Integration"
      ]
    },
    {
      name: "Android App Development",
      description: "Custom Android applications optimized for the diverse Android ecosystem, built with Kotlin and Jetpack Compose for exceptional functionality",
      icon: <AndroidIcon fontSize="large" />,
      features: [
        "Native Android Development with Kotlin",
        "Jetpack Compose UI",
        "Google Play Store Deployment",
        "Material Design Implementation",
        "Support for Diverse Android Devices"
      ]
    },
    {
      name: "Cross-Platform Development",
      description: "Cost-effective solutions that work on multiple platforms from a single codebase using React Native or Flutter for faster time-to-market",
      icon: <DevicesIcon fontSize="large" />,
      features: [
        "React Native Development",
        "Flutter App Solutions",
        "Shared Codebase Efficiency",
        "Consistent Experience Across Platforms",
        "Faster Development Cycles"
      ]
    },
    {
      name: "Progressive Web Apps",
      description: "Web applications with native-like capabilities that offer offline functionality, push notifications, and home screen installation",
      icon: <WebIcon fontSize="large" />,
      features: [
        "Offline Functionality",
        "Push Notifications",
        "Responsive Designs",
        "App-like Experience",
        "Reduced Development Costs"
      ]
    }
  ];

  // Industry solutions
  const industrySolutions = [
    {
      name: "E-commerce & Retail",
      description: "Mobile shopping experiences with secure payment processing, product catalogs, and personalized recommendations",
      icon: <ShoppingCartIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    },
    {
      name: "Healthcare & Telemedicine",
      description: "HIPAA-compliant applications for patient management, telemedicine consultations, and health monitoring",
      icon: <LocalHospitalIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    },
    {
      name: "Financial Services",
      description: "Secure mobile banking, payment solutions, and financial management applications with robust security features",
      icon: <AccountBalanceIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    },
    {
      name: "Food & Delivery",
      description: "Restaurant ordering, food delivery tracking, and logistics management applications with real-time updates",
      icon: <RestaurantIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    },
    {
      name: "Transportation & Logistics",
      description: "Ride-sharing, fleet management, and logistics applications with GPS tracking and route optimization",
      icon: <DirectionsCarIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    },
    {
      name: "Travel & Hospitality",
      description: "Travel booking, itinerary management, and hospitality service applications with integrated mapping",
      icon: <FlightTakeoffIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    },
    {
      name: "Gaming & Entertainment",
      description: "Engaging gaming applications, AR/VR experiences, and entertainment platforms with immersive content",
      icon: <SportsEsportsIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    },
    {
      name: "Education & E-Learning",
      description: "Interactive learning applications with progress tracking, assessments, and multimedia content delivery",
      icon: <SchoolIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    }
  ];

  // Development process
  const developmentProcess = [
    {
      step: "Discovery & Strategy",
      description: "We analyze your business requirements, identify user needs, and develop a comprehensive mobile app strategy",
      icon: <TuneIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    },
    {
      step: "UX/UI Design",
      description: "Our designers create intuitive, engaging user experiences with platform-specific design patterns",
      icon: <ViewQuiltIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    },
    {
      step: "Development",
      description: "Our developers build your application using clean, maintainable code and modern development practices",
      icon: <MemoryIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    },
    {
      step: "Testing & QA",
      description: "Rigorous testing across devices and scenarios ensures your app is reliable, secure, and bug-free",
      icon: <BugReportIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    },
    {
      step: "Deployment",
      description: "We handle the submission process to app stores and ensure compliance with platform guidelines",
      icon: <CloudUploadIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    },
    {
      step: "Maintenance & Support",
      description: "Ongoing technical support, updates, and enhancements keep your app current and competitive",
      icon: <SupportAgentIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    }
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
          color: 'white',
          position: 'relative',
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom textAlign="center">
              Mobile App Development
            </Typography>
            <Typography variant="h6" paragraph align="center" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.9 }}>
              Transforming ideas into exceptional mobile experiences
            </Typography>
          </motion.div>
        </Container>
        
        {/* Decorative Elements */}
        <Box sx={{
          position: 'absolute',
          top: 60,
          right: 120,
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: 40,
          left: 100,
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
      </Box>

      {/* Overview Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom 
                fontWeight="bold"
                color="#0c2340"
              >
                Mobile Solutions for the Modern World
              </Typography>
              <Typography paragraph>
                Mobile applications are transforming how businesses engage with customers, enhance productivity,
                streamline operations, and stay competitive. Right Track Technologies Limited delivers high-quality mobile app development
                services that combine innovative design with powerful functionality to create impactful mobile experiences.
              </Typography>
              <Typography paragraph>
                Our team of experienced mobile developers combines technical expertise with creative design to create mobile 
                solutions that are not only visually appealing but also performant, secure, and scalable. We follow industry 
                best practices and use the latest technologies to ensure your mobile application meets the highest standards 
                and delivers exceptional user experiences.
              </Typography>
              <Typography paragraph>
                Whether you need a consumer-facing app, an enterprise mobility solution, or a complex multi-platform system, 
                our comprehensive mobile app development services are tailored to your specific business requirements and user needs.
              </Typography>
            </motion.div>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Box 
                component="img"
                src="https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                alt="Mobile App Development"
                sx={{
                  width: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* Platforms Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
            >
              Mobile Development Platforms
            </Typography>
            <Typography 
              textAlign="center" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto', 
                mb: 6 
              }}
            >
              Comprehensive mobile solutions across all major platforms
            </Typography>
          </motion.div>

          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: 4,
          }}>
            {platforms.map((platform, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    p: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ 
                      mr: 2, 
                      color: theme.palette.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      {platform.icon}
                    </Box>
                    <Typography variant="h5" component="h3" fontWeight="bold">
                      {platform.name}
                    </Typography>
                  </Box>
                  <Typography color="text.secondary" paragraph>
                    {platform.description}
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Key Features:
                    </Typography>
                    <List dense disablePadding>
                      {platform.features.map((feature, idx) => (
                        <ListItem key={idx} disableGutters sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckCircleOutlineIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                          </ListItemIcon>
                          <ListItemText primary={feature} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Paper>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Development Process Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            fontWeight="bold"
            color="#0c2340"
            textAlign="center"
          >
            Our Mobile App Development Process
          </Typography>
          <Typography 
            textAlign="center" 
            sx={{ 
              maxWidth: 800, 
              mx: 'auto', 
              mb: 6 
            }}
          >
            A systematic approach to delivering exceptional mobile applications
          </Typography>
        </motion.div>
      
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 4,
          justifyContent: 'center'
        }}>
          {developmentProcess.map((process, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              style={{ width: isMobile ? '100%' : 'calc(33.333% - 16px)' }}
            >
              <Paper
                elevation={2}
                sx={{
                  p: 4,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  position: 'relative',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <Typography 
                  variant="h2" 
                  component="div" 
                  sx={{ 
                    position: 'absolute', 
                    top: -15, 
                    right: 10, 
                    opacity: 0.1,
                    fontWeight: 'bold',
                    color: theme.palette.primary.main,
                    fontSize: '5rem',
                    zIndex: 0
                  }}
                >
                  {index + 1}
                </Typography>
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {process.icon}
                  </Box>
                  <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                    {process.step}
                  </Typography>
                  <Typography color="text.secondary">
                    {process.description}
                  </Typography>
                </Box>
              </Paper>
            </motion.div>
          ))}
        </Box>
      </Container>

      {/* Industry Solutions Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
            >
              Industry-Specific Mobile Solutions
            </Typography>
            <Typography 
              textAlign="center" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto', 
                mb: 6 
              }}
            >
              Tailored mobile applications for diverse industry needs
            </Typography>
          </motion.div>

          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: 3,
          }}>
            {industrySolutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    height: '100%',
                    display: 'flex',
                    borderRadius: 3,
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <Box sx={{ mr: 2 }}>
                    {solution.icon}
                  </Box>
                  <Box>
                    <Typography variant="h6" component="h3" fontWeight="bold" gutterBottom>
                      {solution.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {solution.description}
                    </Typography>
                  </Box>
                </Paper>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Key Features Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            fontWeight="bold"
            color="#0c2340"
            textAlign="center"
          >
            Why Choose Our Mobile Development Services
          </Typography>
          <Typography 
            textAlign="center" 
            sx={{ 
              maxWidth: 800, 
              mx: 'auto', 
              mb: 6 
            }}
          >
            Delivering superior mobile experiences with unmatched expertise
          </Typography>
        </motion.div>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <Grid container spacing={4}>
            {[
              {
                title: "User-Centric Design",
                description: "Intuitive interfaces and smooth user experiences that keep users engaged and satisfied",
                icon: <AutoAwesomeIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
              },
              {
                title: "High Performance",
                description: "Fast, responsive applications optimized for performance across all target devices",
                icon: <SpeedIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
              },
              {
                title: "Security-First Approach",
                description: "Robust security measures to protect user data and ensure compliance with regulations",
                icon: <SecurityIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
              },
              {
                title: "Scalable Architecture",
                description: "Future-proof solutions that can grow with your business and adapt to changing needs",
                icon: <DevicesIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
              }
            ].map((feature, index) => (
              <Box key={index} sx={{ width: { xs: '100%', md: '50%' }, px: 2, mb: 4 }}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Box sx={{ mr: 2, mt: 0.5 }}>
                      {feature.icon}
                    </Box>
                    <Box>
                      <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                        {feature.title}
                      </Typography>
                      <Typography color="text.secondary">
                        {feature.description}
                      </Typography>
                    </Box>
                  </Box>
                </motion.div>
              </Box>
            ))}
          </Grid>
        </Box>
      </Container>

      {/* Call to Action Section */}
      <Box sx={{ bgcolor: '#0c2340', color: 'white', py: 10 }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            style={{ textAlign: 'center' }}
          >
            <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
              Ready to Build Your Mobile Application?
            </Typography>
            <Typography sx={{ mb: 4, opacity: 0.9 }}>
              Contact us today to discuss your mobile app development project and discover how 
              our expert team can bring your vision to life.
            </Typography>
            <Button
              component={Link}
              to="/contact"
              variant="contained"
              color="primary"
              size="large"
              sx={{
                bgcolor: 'white',
                color: '#0c2340',
                px: 4,
                py: 1.5,
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)',
                },
              }}
            >
              Start Your Project
            </Button>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default MobileAppDevelopment; 